import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

const SelectOptionPicker = ({ field, single = false, multi = false, value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const containerRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isOpen]);

    const rawOptions = field?.options || [];
    // Curated palette for auto-coloring string options
    const OPTION_PALETTE = ['#2196F3', '#4CAF50', '#FF9800', '#E91E63', '#9C27B0', '#00BCD4', '#FF5722', '#607D8B', '#8BC34A', '#795548'];
    // Normalize: options can be strings ['New', 'Done'] or objects [{id, label, color}]
    const options = rawOptions.map((opt, idx) => {
        if (typeof opt === 'string') return { id: opt, value: opt, label: opt, color: OPTION_PALETTE[idx % OPTION_PALETTE.length] };
        return { id: opt.id || opt.value || opt.label, value: opt.value || opt.id || opt.label, label: opt.label || opt.value || opt.id, color: opt.color || OPTION_PALETTE[idx % OPTION_PALETTE.length] };
    });
    const isMulti = multi || (!single && field.type === 'multi_select');
    
    // Value parsing
    const valArray = Array.isArray(value) ? value : (value ? [value] : []);

    const toggleOption = (optId) => {
        if (isMulti) {
            const newVals = valArray.includes(optId)
                ? valArray.filter(id => id !== optId)
                : [...valArray, optId];
            onChange(newVals);
        } else {
            onChange(valArray.includes(optId) ? '' : optId);
            setIsOpen(false);
        }
    };

    // Rendering chips/text in the input field
    const renderSelectedChips = () => {
        if (valArray.length === 0) {
            return <span style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>Select option{isMulti ? 's' : ''}...</span>;
        }

        if (!isMulti) {
            // Single select: pure text with a subtle dot, no surrounding chip wrapper
            const id = valArray[0];
            const opt = options.find(o => o.id === id || o.value === id) || { label: id, color: '#cbd5e1' };
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-primary)', width: '100%' }}>
                    <span style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: opt.color || '#cbd5e1',
                        flexShrink: 0
                    }} />
                    <span style={{ fontWeight: 400, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{opt.label}</span>
                </div>
            );
        }

        // Multi select: elegant, neutral gray chips with a small colored dot
        return (
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', width: '100%' }}>
                {valArray.map(id => {
                    const opt = options.find(o => o.id === id || o.value === id) || { label: id, color: '#cbd5e1' };
                    return (
                        <div key={id} style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            background: 'var(--bg-subtle)',
                            border: '1px solid var(--border-subtle)',
                            color: 'var(--text-primary)',
                            padding: '2px 6px', borderRadius: '4px', fontSize: '12px', fontWeight: 500,
                            userSelect: 'none'
                        }}>
                            <span style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                background: opt.color || '#cbd5e1',
                                flexShrink: 0
                            }} />
                            <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{opt.label}</span>
                            <div 
                                style={{ display: 'flex', cursor: 'pointer', opacity: 0.6, marginLeft: '2px' }} 
                                onClick={(e) => { e.stopPropagation(); toggleOption(id); }}
                                className="hover-opacity-100"
                            >
                                <ObliqueIcon name="close" size={12} />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%', minWidth: '160px' }}>
            {/* Trigger Button - matches other input field heights */}
            <div 
                onClick={() => setIsOpen(!isOpen)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={{
                    minHeight: '28px', width: '100%',
                    padding: '2px 8px',
                    border: `1px solid ${isOpen || isHovered ? 'var(--border-strong)' : 'var(--border)'}`,
                    borderRadius: '4px',
                    background: 'var(--bg-surface)',
                    display: 'flex', alignItems: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.15s ease-in-out'
                }}
            >
                {renderSelectedChips()}
            </div>

            {/* Popover */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 4, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 4, scale: 0.98 }}
                        transition={{ duration: 0.15 }}
                        style={{
                            position: 'absolute', top: 'calc(100% + 4px)', left: 0,
                            minWidth: '200px', maxHeight: '240px', overflowY: 'auto',
                            background: 'var(--bg-surface)', border: '1px solid var(--border)',
                            borderRadius: '8px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                            zIndex: 3000, padding: '6px'
                        }}
                        className="custom-scrollbar"
                    >
                        {options.length === 0 ? (
                            <div style={{ padding: '8px', fontSize: '12px', color: 'var(--text-tertiary)', textAlign: 'center' }}>
                                No options available
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                {options.map(opt => {
                                    const valId = opt.id || opt.value;
                                    const isSelected = valArray.includes(valId);
                                    return (
                                        <div
                                            key={valId}
                                            onClick={() => toggleOption(valId)}
                                            style={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                padding: '6px 8px', borderRadius: '6px',
                                                cursor: 'pointer',
                                                background: isSelected ? 'var(--bg-muted)' : 'transparent',
                                                transition: 'background 0.1s'
                                            }}
                                            className="hover-bg-muted"
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                                                {/* Clean 8px colored dot instead of full paint */}
                                                <span style={{
                                                    width: '8px',
                                                    height: '8px',
                                                    borderRadius: '50%',
                                                    background: opt.color || '#cbd5e1',
                                                    flexShrink: 0
                                                }} />
                                                <span style={{
                                                    fontSize: '13px',
                                                    color: 'var(--text-primary)',
                                                    fontWeight: isSelected ? 500 : 400,
                                                    textOverflow: 'ellipsis',
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {opt.label}
                                                </span>
                                            </div>
                                            {isSelected && (
                                                <div style={{ display: 'flex', alignItems: 'center', color: 'var(--accent)', flexShrink: 0 }}>
                                                    <ObliqueIcon name="check" size={14} />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SelectOptionPicker;
